// COP Copilot — the request_evac STUB (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// Wave-A safety posture: request_evac is a HOST-GATED CommandSpec with NO auto-dispatch.
// It reroutes takbot's broker.handleRequestEvac (which called deps.map.placeEvac directly)
// onto the COP's contributes-only path: the capability NEVER touches the engine; the host
// paints the marker from a published envelope (cop.ts paintRow → engine.upsertEntities).
//
// WHY risk:'irreversible' (the deliberate divergence from takbot's 'reversible'):
//   requiresHumanGate() (CapabilityHost.ts:180) gates EXACTLY `irreversible`/unknown, and
//   dispatchCommand (CapabilityHost.ts:425-432) throws ErrHumanGateRequired unless a HUMAN
//   actor + a satisfied host confirmation hook are present. There is NO confirm hook wired in
//   Wave A, so run() can NEVER fire from the model: the copilot/agent can SURFACE the evac
//   intent, but the host human-gate blocks dispatch BY CONSTRUCTION. No marker is placed,
//   nothing leaves the node. (takbot's 'reversible' self-dispatch is the OPPOSITE posture and
//   is explicitly out of scope for Wave A.)
//
// actors:['human'] (+ commandActors in copilotManifest.ts) keep an ai-agent from even invoking
// it. The position is resolved HOST-SIDE from a placeholder selfLocation (HostContext.selfLocation
// is upstream-unauthored — stubbed here); the capability/agent NEVER supplies lat/lng, and
// additionalProperties:false rejects any coordinate arg.
//
// SCHEMA NOTE: no 'evac' EnvelopeKind exists (packages/schema/ts/envelope.ts). The stub reuses
// kind:'alert' + properties.subtype:'evac' (no schema bump — a deferred steward decision).
//
// Holds NO React, never writes the engine, imports ONLY types from the C3 SDK.

import type {
  CommandSpec,
  CanonicalEnvelope,
  SecurityLabels,
} from "../../../../packages/contracts-ts/src/index.ts";

/** Stable command id (host-gated, human-only). */
export const REQUEST_EVAC_CMD_ID = "scop:cmd:request-evac";

/** Evac priority — the only operator-supplied field besides a free-text note. */
export type EvacPriority = "routine" | "priority" | "urgent";
const DEFAULT_EVAC_PRIORITY: EvacPriority = "priority";

/** WHERE the evac position came from — STATED in the request (task rail: the alert
 *  carries a position only if sharing was on ("own-position") or the operator
 *  explicitly confirmed a point in the composer ("selected-point"/"map-center");
 *  the composer displays the resolved point + source before send). */
export type EvacLocationSource = "own-position" | "selected-point" | "map-center";

/** A host-owned self-location accessor. HostContext.selfLocation is upstream-unauthored, so the
 *  host injects this stub; the capability/agent NEVER supplies coordinates. */
export interface SelfLocation {
  /** [lng, lat] GeoJSON order. */
  position: [number, number];
  /** Optional human label for the location (e.g. callsign / grid). */
  label?: string;
  /** Where the position came from (stated in the request when present). The
   *  Wave-A placeholder carries none; the live host path always sets it. */
  source?: EvacLocationSource;
}

/** A placeholder self-location (Wave A; no live geolocation). The host now wires a
 *  LIVE accessor (cop.ts → ownPosition.currentFix()) that falls back to THIS when
 *  the operator is not sharing true location — the C3 command itself remains
 *  human-gated (no auto-dispatch), so this placeholder is never published live. */
export function placeholderSelfLocation(): SelfLocation {
  return { position: [56.2, 26.5], label: "SELF (placeholder)" };
}

/** The labels for an evac alert. UNCLASSIFIED + a life-safety policy ref; a real signalling
 *  capability re-tiers per EVAC-tak-tdf-coordination.md §2.2/§4 (a steward call, not Wave A). */
function evacLabels(): SecurityLabels {
  return {
    classification: "UNCLASSIFIED",
    // `[]` = public / unrestricted (broadest life-safety audience). A coalition tag like NATO
    // is NOT a legal defense.local releasability — it would fail validateMarkings
    // (ILLEGAL_RELEASABILITY) and hide the alert fail-closed on the read path.
    releasability: [],
    mission: [],
    caveats: [],
    originator_control: false,
    policy_ref: "policy://life-safety/evac",
    payload_mode: "clear",
    leakage_profile: "clear",
  };
}

/** How long an evac request stays valid on the picture before aging out of the
 *  recency window (a request unresolved after this long needs re-raising anyway). */
const EVAC_STALE_MS = 60 * 60_000;

/** Build the kind:'alert' + properties.subtype:'evac' envelope the HOST paints. The position
 *  comes ONLY from the host self-location; the capability/agent never sets it.
 *
 *  ⭐ MARKING DECISION: UNCLASSIFIED / releasable-to-all (evacLabels) — a life-safety
 *  broadcast wants the broadest cleared audience. See EVAC-tak-tdf-coordination.md §2.2.
 *
 *  Shape parity with the live alert feeds (NWS/USGS): kind:"alert" + properties
 *  {name, urgency, …} so the host's alert fold (engine.subscribe kinds:["alert"] →
 *  status.alertLog) and the alert glyph family pick it up with NO special-casing —
 *  and `properties.priority` drives the S1 egress <emergency priority> facet. */
export function buildEvacEnvelope(
  loc: SelfLocation,
  opts: { priority: EvacPriority; note?: string; initiator: string },
): CanonicalEnvelope {
  const time = new Date().toISOString();
  return {
    id: `${REQUEST_EVAC_CMD_ID}:${opts.initiator}:${Date.now()}`,
    schema: "scop.envelope/1.0",
    kind: "alert",
    time,
    valid_time: { start: time, stale: new Date(Date.now() + EVAC_STALE_MS).toISOString() },
    geometry: { type: "Point", coordinates: loc.position },
    properties: {
      // The alert-log row label (AlertRow.label = properties.name) — carry the
      // urgency in the label so the panel reads it at a glance.
      name: `EVAC request · ${opts.priority.toUpperCase()}`,
      type: "alert",
      subtype: "evac",
      priority: opts.priority,
      // NWS-parity field name so alert surfaces that read `urgency` see the same
      // value as `priority` (one operator choice, two consumer vocabularies).
      urgency: opts.priority,
      // The evac requester is a coalition member — a friendly alert (renders in
      // the friendly palette; kind:"alert" already crowns the label/icon rank).
      affiliation: "friend",
      initiator: opts.initiator,
      ...(loc.source ? { location_source: loc.source } : {}),
      ...(loc.label ? { location_label: loc.label } : {}),
      ...(opts.note ? { note: opts.note } : {}),
    },
    security: evacLabels(),
    provenance: { originator: opts.initiator, adapter: "cop-copilot-evac", received_at: time, chain: [] },
    payload: {
      clear: {
        subtype: "evac",
        priority: opts.priority,
        ...(loc.source ? { location_source: loc.source } : {}),
        ...(opts.note ? { note: opts.note } : {}),
      },
    },
  };
}

/** Construction deps for the evac command (host-injected; the capability supplies neither the
 *  publish port nor the location to the agent). */
export interface EvacCommandDeps {
  /** Resolve the HOST-OWNED self-location (placeholder in Wave A). */
  selfLocation: () => SelfLocation;
  /** Publish the evac envelope onto the bus; the HOST paints the marker (sole writer). */
  publish: (env: CanonicalEnvelope) => Promise<void>;
}

/** The request_evac CommandSpec. risk:'irreversible' + actors:['human'] force the host human-gate;
 *  run() can never fire from the model in Wave A (no confirm hook).
 *
 *  CARRIED GUARDRAILS (takbot, as PREP for Wave B live dispatch):
 *   - single-active de-dup (one active evac per session) — wired with the host-owned active state.
 *   - host-only clear (no agent retract verb exists).
 *   - initiator labeling (host button vs advisory agent request).
 *  These are deferred to EVAC live dispatch + risk-tier ratification (Wave B item 4). */
export function createRequestEvacCommand(deps: EvacCommandDeps): CommandSpec {
  return {
    id: REQUEST_EVAC_CMD_ID,
    title: "Request EVAC",
    category: "Map",
    risk: "irreversible",
    actors: ["human"],
    input: {
      type: "object",
      properties: {
        note: { type: "string" },
        priority: { enum: ["routine", "priority", "urgent"] },
      },
      additionalProperties: false,
    },
    // PREP-only in Wave A: the host human-gate blocks dispatch before this body runs (no confirm
    // hook is wired). It is authored now so the live wiring (Wave B item 4) is a config change,
    // not new code: it resolves position HOST-SIDE and publishes the alert(subtype:'evac') envelope
    // the host paints. additionalProperties:false already rejected any coordinate arg.
    async run(args?: unknown): Promise<void> {
      const a = (args ?? {}) as { note?: string; priority?: EvacPriority };
      const loc = deps.selfLocation();
      const priority: EvacPriority =
        a.priority === "routine" || a.priority === "priority" || a.priority === "urgent"
          ? a.priority
          : DEFAULT_EVAC_PRIORITY;
      const env = buildEvacEnvelope(loc, {
        priority,
        ...(typeof a.note === "string" ? { note: a.note } : {}),
        initiator: "human",
      });
      await deps.publish(env);
      // AUDIT CRITICAL: the live audit seam (S7 Severity/ACTION) is authored upstream in Wave B
      // (EVAC §2.4); a newly-raised evac is a CRITICAL trace, a de-dup'd repeat is plain.
    },
  };
}
